import { useEffect, useState } from 'react';
import { Customer } from '@hiwaldo/sdk/interfaces';
import { Route } from '../../../enums';
import { useDispatch, useSelector } from 'react-redux';
import { Region } from '@hiwaldo/sdk';
import { AppStorageKey, appStorage, regionCountryCode } from '../../../helpers';
import {
  StoreState,
  useBasketTokenGetEffect,
  useBasketAgreementEffect,
  useBasketCompleteEffect,
  CheckoutDataState,
  checkoutDataSlice,
} from '../../../state';

export interface CheckoutPaypalSubmitArgs {
  customer: Customer;
  amount: number;
}

export interface CheckoutPaypal {
  valid: boolean;
  processing: boolean;
  success: boolean;
  error?: string;
  submit: (args: CheckoutPaypalSubmitArgs) => void;
  agreement: (token: string) => void;
  reset: () => void;
}

export function useCheckoutPaypalEffect(
  region: Region,
  domain: string,
  url: string
): CheckoutPaypal {
  const dispatch = useDispatch();

  const country = regionCountryCode(region);
  let currency = 'USD';

  switch (region) {
    case 'UK':
      currency = 'GBP';
      break;
    case 'SE':
      currency = 'SEK';
      break;
    case 'EU':
      currency = 'EUR';
      break;
  }

  const {
    basketDataState,
    checkoutDataState,
    customerDataState,
    cartState,
    funnelState,
  } = useSelector((state: StoreState) => ({
    basketDataState: state.data.basket,
    checkoutDataState: state.data.checkout,
    customerDataState: state.data.customer,
    cartState: state.orders.cart,
    funnelState: state.orders.funnel,
  }));

  const basketToken = useBasketTokenGetEffect();
  const basketAgreement = useBasketAgreementEffect();
  const basketComplete = useBasketCompleteEffect();

  const [paypalValid, setPaypalValid] = useState(false);

  const [paypalProcessing, setPaypalProcessing] = useState(false);
  const [paypalSuccess, setPaypalSuccess] = useState<boolean>(false);
  const [paypalError, setPaypalError] = useState<string | undefined>(undefined);

  const paypalSubmit = (args: CheckoutPaypalSubmitArgs) => {
    setPaypalProcessing(true);

    const { customer, amount } = args;

    basketToken.enact({
      orderTotal: amount,
      currency,
      returnUrl: domain + Route.CHECKOUT_PAYPAL_RETURN_URL,
      cancelUrl: domain + Route.CHECKOUT_PAYPAL_CANCEL_URL,
      address: {
        name: `${customer!.firstName} ${customer!.lastName}`,
        line1: customer!.billingAddress!.line1,
        line2: customer?.billingAddress?.line2 || ' -',
        city: customer!.billingAddress!.city,
        state: customer!.billingAddress!.state,
        country: country || 'GB',
        postalCode: customer!.billingAddress!.postalCode,
      },
    });
  };

  const paypalAgreement = (token: string) => {
    setPaypalProcessing(true);

    if (customerDataState.customer) {
      basketAgreement.enact(+customerDataState.customer.id, token);
    } else {
      setPaypalValid(false);
      setPaypalProcessing(false);
      setPaypalSuccess(false);
      setPaypalError('Failed to create billing agreement: Missing customer');
    }
  };

  const paypalReset = () => {
    setPaypalValid(false);
    setPaypalProcessing(false);
    setPaypalSuccess(false);
    setPaypalError(undefined);
  };

  useEffect(() => {
    if (!basketToken.loading) {
      if (basketToken.success && basketDataState.token) {
        basketToken.clear();
        window.open(`${url}${basketDataState.token}`, '_self');
      }

      if (basketToken.error) {
        basketToken.clear();
        setPaypalValid(false);
        setPaypalProcessing(false);
        setPaypalSuccess(false);
        setPaypalError(basketToken.error);
      }
    }

    if (!basketAgreement.loading) {
      if (basketAgreement.success && basketDataState.cart?.clientId) {
        basketAgreement.clear();
        setPaypalValid(true);
        basketComplete.enact(basketDataState.cart.clientId);
      }

      if (basketAgreement.error) {
        basketAgreement.clear();
        setPaypalValid(false);
        setPaypalProcessing(false);
        setPaypalSuccess(false);
        setPaypalError(basketAgreement.error);
      }
    }

    if (!basketComplete.loading) {
      if (basketComplete.success && basketDataState.token) {
        basketComplete.clear();

        appStorage.set(AppStorageKey.PAID_CART, cartState);
        appStorage.set(AppStorageKey.COMPLETED_FUNNEL, funnelState);

        const updatedCheckoutDataState: CheckoutDataState = Object.assign({}, {
          ...checkoutDataState,
          completedPayment: true,
        } as CheckoutDataState);

        dispatch(checkoutDataSlice.actions.update(updatedCheckoutDataState));
        setPaypalProcessing(false);
        setPaypalSuccess(true);
      }

      if (basketComplete.error) {
        basketComplete.clear();
        setPaypalProcessing(false);
        setPaypalSuccess(false);
      }
    }
  }, [basketDataState, basketToken, basketAgreement, basketComplete]);

  return {
    valid: paypalValid,
    processing: paypalProcessing,
    success: paypalSuccess,
    error: paypalError,
    submit: paypalSubmit,
    agreement: paypalAgreement,
    reset: paypalReset,
  };
}
